import {
  Column,
  TableState,
  Updater,
  VisibilityState,
} from "@tanstack/react-table";
import { useEffect, useRef } from "react";

interface UseColumnVisibilityProps {
  tableName: string;
  setColumnVisibility: (updater: Updater<VisibilityState>) => void;
  defaultState?: Partial<TableState>;
  state: Partial<TableState>;
}

interface UseColumnVisibilityReturn {
  onColumnVisibilityChange: (columnName: string, visibility: boolean) => void;
  onChangeVisibilityDefaultState: { [key: string]: undefined };
}

export const useColumnVisibility = ({
  tableName,
  setColumnVisibility,
  defaultState = {},
  state,
}: UseColumnVisibilityProps): UseColumnVisibilityReturn => {
  const onChangeVisibilityDefaultState = useRef<{ [key: string]: undefined }>(
    {}
  ).current;

  // LocalStorage helpers
  const getStoredVisibility = (): { [key: string]: boolean } | null => {
    try {
      const stored = localStorage.getItem(`${tableName}Visibility`);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  };

  const setStoredVisibility = (visibility: {
    [key: string]: boolean | undefined;
  }): void => {
    localStorage.setItem(`${tableName}Visibility`, JSON.stringify(visibility));
  };

  // Initialize column visibility on mount
  useEffect(() => {
    // Load and apply column visibility from localStorage
    const storedVisibility = getStoredVisibility();
    if (storedVisibility) {
      const finalVisibility = {
        ...storedVisibility,
        ...defaultState.columnVisibility,
      };
      setColumnVisibility(finalVisibility);
    }

    // Setup unchangeable visibility state
    if (defaultState.columnVisibility) {
      for (let key in defaultState.columnVisibility) {
        onChangeVisibilityDefaultState[key] = undefined;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle column visibility changes
  const onColumnVisibilityChange = (
    columnName: string,
    visibility: boolean
  ): void => {
    const newVisibility = {
      ...state.columnVisibility,
      [columnName]: visibility,
      ...onChangeVisibilityDefaultState,
    };
    setStoredVisibility(newVisibility);
  };

  return {
    onColumnVisibilityChange,
    onChangeVisibilityDefaultState,
  };
};
